import { ChevronDownIcon, ChevronUpIcon } from '@radix-ui/react-icons';
import {
  Badge,
  Box,
  Card,
  Flex,
  Grid,
  IconButton,
  Spinner,
  Text,
} from '@radix-ui/themes';
import { MachineContextHelper } from 'classes/helpers/machine-context.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import { lightFormat, parseISO } from 'date-fns';
import { LOCAL_DATETIME_FORMAT } from 'lib_ts/enums/datetime.enums';
import {
  FeatureFlagAuditAction,
  FeatureFlagCriterionType,
  FeatureFlagEnvironment,
  FeatureFlagMachineMode,
} from 'lib_ts/enums/feature-flags.enums';
import {
  IFeatureFlag,
  IFeatureFlagAuditLog,
  IFeatureFlagCriterion,
} from 'lib_ts/interfaces/feature-flags/i-feature-flag';
import { useEffect, useState } from 'react';
import { AdminMachinesService } from 'services/admin/machines.service';
import { AdminTeamsService } from 'services/admin/teams.service';
import { AdminUsersService } from 'services/admin/users.service';
import { PlatformConsoleService } from 'services/platform-console.service';

const COMPONENT_NAME = 'FeatureFlagAuditHistory';

interface IProps {
  flag: IFeatureFlag;
  environment: FeatureFlagEnvironment;
}

/**
 * id -> human label maps so audit criteria render as emails / team names /
 * machine labels instead of raw ids. Sourced from the same admin services the
 * criteria composer uses; any unresolved id falls back to the raw value.
 */
interface IAuditLookups {
  users: Map<string, string>;
  teams: Map<string, string>;
  machines: Map<string, string>;
}

const EMPTY_LOOKUPS: IAuditLookups = {
  users: new Map(),
  teams: new Map(),
  machines: new Map(),
};

/**
 * Renders each id as "Pretty Label (rawId)" so the human name is legible while
 * the raw id (needed when wiring flags up in code) stays visible/copyable.
 * Falls back to the bare id when no label resolves.
 */
const resolveIds = (ids: string[], lookup: Map<string, string>): string =>
  ids
    .map((id) => {
      const label = lookup.get(id);
      return label ? `${label} (${id})` : id;
    })
    .join(', ');

const ACTION_COLORS: Record<
  FeatureFlagAuditAction,
  React.ComponentProps<typeof Badge>['color']
> = {
  [FeatureFlagAuditAction.Create]: 'green',
  [FeatureFlagAuditAction.Update]: 'blue',
  [FeatureFlagAuditAction.Toggle]: 'amber',
  [FeatureFlagAuditAction.Duplicate]: 'indigo',
  [FeatureFlagAuditAction.Delete]: 'red',
};

/** readable "dimension: values" line for a single targeting criterion */
const describeCriterion = (
  criterion: IFeatureFlagCriterion,
  lookups: IAuditLookups
): { label: string; values: string } => {
  switch (criterion.type) {
    case FeatureFlagCriterionType.Role:
      return { label: 'Role', values: criterion.roles.join(', ') };

    case FeatureFlagCriterionType.User:
      return {
        label: 'User',
        values: resolveIds(criterion.userIds, lookups.users),
      };

    case FeatureFlagCriterionType.Team:
      return {
        label: 'Team',
        values: resolveIds(criterion.teamIds, lookups.teams),
      };

    case FeatureFlagCriterionType.Machine:
      return {
        label: 'Machine',
        values:
          criterion.mode === FeatureFlagMachineMode.All
            ? 'all machines'
            : resolveIds(criterion.machineIds ?? [], lookups.machines),
      };

    default:
      return { label: 'Unknown', values: '' };
  }
};

/** full (non-truncated) view of a before/after state snapshot */
const SnapshotView = ({
  snapshot,
  lookups,
}: {
  snapshot?: Partial<IFeatureFlag>;
  lookups: IAuditLookups;
}) => {
  if (!snapshot) {
    return (
      <Text size="1" color="gray">
        —
      </Text>
    );
  }

  return (
    <Flex direction="column" gap="2">
      {snapshot.enabled !== undefined && (
        <Text size="1">
          <Text weight="bold">Enabled: </Text>
          {snapshot.enabled ? 'Yes' : 'No'}
        </Text>
      )}

      {snapshot.description !== undefined && (
        <Text
          size="1"
          style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
        >
          <Text weight="bold">Description: </Text>
          {snapshot.description || '—'}
        </Text>
      )}

      {snapshot.criteria !== undefined && (
        <Box>
          <Text size="1" weight="bold">
            Criteria:
          </Text>
          {snapshot.criteria.length === 0 ? (
            <Text size="1" color="gray">
              {' '}
              None
            </Text>
          ) : (
            <Flex direction="column" gap="1" mt="1">
              {snapshot.criteria.map((criterion, i) => {
                const described = describeCriterion(criterion, lookups);

                return (
                  <Text key={i} size="1" style={{ wordBreak: 'break-word' }}>
                    <Text weight="bold">{described.label}: </Text>
                    {described.values || '—'}
                  </Text>
                );
              })}
            </Flex>
          )}
        </Box>
      )}
    </Flex>
  );
};

const AuditRow = ({
  log,
  lookups,
}: {
  log: IFeatureFlagAuditLog;
  lookups: IAuditLookups;
}) => {
  const [open, setOpen] = useState(false);

  const when = log._changed
    ? lightFormat(parseISO(log._changed), LOCAL_DATETIME_FORMAT)
    : '—';

  return (
    <Card>
      <Flex
        align="center"
        justify="between"
        gap="3"
        style={{ cursor: 'pointer' }}
        onClick={() => setOpen((prev) => !prev)}
      >
        <Flex align="center" gap="3" wrap="wrap">
          <Badge color={ACTION_COLORS[log.action]} variant="soft">
            {log.action}
          </Badge>
          <Text size="1">{when}</Text>
          <Text size="1" color="gray">
            {log.actor?.email ?? '—'}
          </Text>
        </Flex>

        <IconButton variant="ghost" color="gray" aria-label="Toggle details">
          {open ? <ChevronUpIcon /> : <ChevronDownIcon />}
        </IconButton>
      </Flex>

      {open && (
        <Box mt="3" pt="3" style={{ borderTop: '1px solid var(--gray-a5)' }}>
          <Grid columns={{ initial: '1', sm: '2' }} gap="4">
            <Box>
              <Text size="1" weight="bold" color="gray">
                Before
              </Text>
              <Box mt="2">
                <SnapshotView snapshot={log.before} lookups={lookups} />
              </Box>
            </Box>
            <Box>
              <Text size="1" weight="bold" color="gray">
                After
              </Text>
              <Box mt="2">
                <SnapshotView snapshot={log.after} lookups={lookups} />
              </Box>
            </Box>
          </Grid>
        </Box>
      )}
    </Card>
  );
};

/**
 * Read-only audit trail for a single flag, rendered as expandable cards so the
 * full before/after state (criteria enumerated, descriptions untruncated) is
 * legible — unlike the old one-line, overflow-clipped dialog view.
 */
export const FeatureFlagAuditHistory = (props: IProps) => {
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState<IFeatureFlagAuditLog[]>([]);
  const [lookups, setLookups] = useState<IAuditLookups>(EMPTY_LOOKUPS);

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);

      try {
        const result =
          await PlatformConsoleService.getInstance().listFeatureFlagAudit(
            props.flag.name,
            props.environment
          );

        if (active) {
          setLogs(result);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      active = false;
    };
  }, [props.flag.name, props.environment]);

  // resolve criteria ids -> labels; non-blocking (rows fall back to raw ids
  // until this resolves). Mirrors the criteria composer's global fetches.
  useEffect(() => {
    let active = true;

    const loadLookups = async () => {
      try {
        const [teams, users, machines] = await Promise.all([
          AdminTeamsService.getInstance().getTeams(true),
          AdminUsersService.getInstance().getUsers(true),
          AdminMachinesService.getInstance().getMachines(true),
        ]);

        if (!active) {
          return;
        }

        setLookups({
          users: new Map(users.map((u) => [u._id, u.email])),
          teams: new Map(teams.map((tm) => [tm._id, tm.name])),
          machines: new Map(
            machines.map((m) => [
              m.machineID,
              MachineContextHelper.getMachineMenuLabel(m),
            ])
          ),
        });
      } catch (e) {
        console.error('Failed to load feature-flag audit id lookups', e);
      }
    };

    void loadLookups();

    return () => {
      active = false;
    };
  }, []);

  return (
    <ErrorBoundary identifier={COMPONENT_NAME}>
      {loading ? (
        <Flex align="center" justify="center" py="6">
          <Spinner />
        </Flex>
      ) : logs.length === 0 ? (
        <Box py="4">
          <Text color="gray">No history for this flag yet.</Text>
        </Box>
      ) : (
        <Box style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          <Flex direction="column" gap="2">
            {logs.map((log) => (
              <AuditRow key={log._id} log={log} lookups={lookups} />
            ))}
          </Flex>
        </Box>
      )}
    </ErrorBoundary>
  );
};
